import type { SeoFaqItem, SeoHowToStep } from "./seo-content";

export const EN_ROUTE_FAQS: Record<string, SeoFaqItem[]> = {
  "/en/docs/": [
    {
      question: "What does the English docs section cover right now?",
      answer:
        "The English docs section currently covers the product model, glossary, FAQ, compare pages, and the first two runnable quick starts. It is meant to answer both conceptual and implementation-intent searches.",
    },
    {
      question: "Where should a new team start?",
      answer:
        "If your team wants to call external capabilities, start with Caller Quick Start. If your team wants to publish a capability, start with Responder Quick Start. Both pages aim to get you to a real local loop first.",
    },
    {
      question: "Why are the quick starts important for SEO and GEO?",
      answer:
        "Because quick starts contain concrete steps, commands, and result shapes. Search engines and answer engines treat these pages as stronger evidence than concept-only pages when users search for implementation intent.",
    },
  ],
  "/en/docs/quick-start-caller/": [
    {
      question: "Who is Caller Quick Start for?",
      answer:
        "It is for agent teams that want to call an external capability through CALL ANYTHING without deploying the full platform first. The page uses Local Mode to prove the minimum caller loop with a real result package.",
    },
    {
      question: "Why start with the example hotline?",
      answer:
        "Because it reduces debugging scope. You first prove that Caller, supervisor, and Responder can complete one stable loop before you spend time integrating a real business capability.",
    },
    {
      question: "What should I inspect after the first call succeeds?",
      answer:
        "Inspect the result_package shape: request status, hotline_id, output, and timing. That is the shared contract your agent will parse across many Hotlines.",
    },
  ],
  "/en/docs/quick-start-responder/": [
    {
      question: "What does Responder Quick Start teach first?",
      answer:
        "It teaches the seller-side minimum loop: start supervisor, enable the Responder role, validate the built-in example hotline, then create your own registration draft with schemas and examples.",
    },
    {
      question: "Do I need a hosted platform to publish a Hotline?",
      answer:
        "No. You can run a Hotline locally or in a private self-hosted environment first. A hosted or self-hosted platform is only needed when you want broader review, routing, or Marketplace-style discovery.",
    },
    {
      question: "Which fields matter most when I publish a Hotline?",
      answer:
        "The most important fields are summary, input_schema, output_schema, examples, and risk notes. They determine whether Callers can understand the capability and whether review can trust the published surface.",
    },
  ],
  "/en/docs/protocol-architecture/": [
    {
      question: "What does the protocol architecture page explain?",
      answer:
        "It isolates the roles of Protocol, Client, Platform, and Selfhost, and explains why CALL ANYTHING separates control-plane operating policy from the data-plane capability exchange.",
    },
    {
      question: "Why move this off the homepage?",
      answer:
        "Because the homepage should anchor product meaning first. The architecture page is for engineering-intent reading and answer-engine retrieval when the question is about system boundaries, not brand narrative.",
    },
    {
      question: "What is the relationship between Platform and Selfhost?",
      answer:
        "They are the same control-plane shape in different deployment forms. Platform is hosted and public-facing; Selfhost is private infrastructure running the same operating surface under your own boundary.",
    },
  ],
  "/en/docs/deployability-profiles/": [
    {
      question: "How is this different from the quick starts?",
      answer:
        "The quick starts help you run the smallest Caller or Responder loop first. Deployability Profiles explain when to use Local Agent Loop, Selfhost Platform, Public Stack, and Management Console, including what is ready now and what remains planned.",
    },
    {
      question: "Why should public-stack fail in some configurations?",
      answer:
        "Because public exposure should fail before unsafe defaults go live. The public-stack preflight checks placeholder secrets, unsafe public origins, compose config, and edge route contracts.",
    },
    {
      question: "Is Selfhost a different product?",
      answer:
        "No. Selfhost is the same Platform control-plane shape deployed privately. The protocol and call shape stay the same; the operator controls data, secrets, logs, console, and exposure boundaries.",
    },
  ],
};

export const EN_ROUTE_HOWTO_STEPS: Record<string, SeoHowToStep[]> = {
  "/en/docs/quick-start-caller/": [
    {
      name: "Install the client and start the supervisor",
      text: "Clone delegated-execution-dev, install dependencies, and start the local supervisor from repos/client.",
    },
    {
      name: "Initialize the session and register a Caller",
      text: "Run first-time setup, obtain an ops session token, and register a local Caller identity.",
    },
    {
      name: "Mount the example Hotline",
      text: "Enable the Responder role locally and add the built-in example hotline so the machine can complete one end-to-end loop.",
    },
    {
      name: "List the catalog and make a call",
      text: "Use the catalog API to confirm the Hotline is visible, then submit one example request.",
    },
    {
      name: "Read the result package",
      text: "Fetch the result and inspect status, hotline_id, output, and timing to confirm the protocol return shape.",
    },
  ],
  "/en/docs/quick-start-responder/": [
    {
      name: "Install the client and start the supervisor",
      text: "Clone delegated-execution-dev, install dependencies in repos/client, and launch the supervisor with an isolated runtime directory.",
    },
    {
      name: "Register and enable the Responder role",
      text: "Create a local or platform-backed identity, then enable the Responder role so the runtime can host Hotline registrations.",
    },
    {
      name: "Validate the example Hotline",
      text: "Add the built-in example hotline first to prove the supervisor and responder loop works before publishing your own capability.",
    },
    {
      name: "Create your own Hotline registration",
      text: "Use a process or HTTP adapter, choose a Hotline ID, and fill in summary, input_schema, output_schema, and examples.",
    },
    {
      name: "Submit review and check status",
      text: "Send the Hotline to the platform review path if needed, then inspect runtime status or console health to confirm it is online.",
    },
  ],
};
